import { useCallback, useEffect, useMemo, useState } from 'react';

import { getEventCastingSummary } from '../../api/eventDetails';
import {
  formatCastingProgressNote,
  normalizeCastingProgressSummary,
} from '../../utils/castingProgress';

type UseEventCastingSummaryOptions = {
  eventId?: string | null;
  enabled: boolean;
};

export default function useEventCastingSummary({
  eventId,
  enabled,
}: UseEventCastingSummaryOptions) {
  const [summary, setSummary] = useState<unknown>(null);

  const refreshCastingSummary = useCallback(async () => {
    if (!eventId || !enabled) {
      setSummary(null);
      return null;
    }
    try {
      const nextSummary = await getEventCastingSummary(eventId);
      setSummary(nextSummary || null);
      return nextSummary || null;
    } catch {
      setSummary(null);
      return null;
    }
  }, [enabled, eventId]);

  useEffect(() => {
    let active = true;
    if (!eventId || !enabled) {
      setSummary(null);
      return undefined;
    }
    getEventCastingSummary(eventId)
      .then((nextSummary) => {
        if (active) setSummary(nextSummary || null);
      })
      .catch(() => {
        if (active) setSummary(null);
      });
    return () => {
      active = false;
    };
  }, [enabled, eventId]);

  const normalized = useMemo(() => normalizeCastingProgressSummary(summary), [summary]);
  return {
    castingProgressPercent: normalized.percent,
    castingProgressNote: formatCastingProgressNote(normalized),
    refreshCastingSummary,
  };
}
